<template>
  <div class="customer-sign">
    <div v-loading="loading" class="cs-card" ref="pdfCard">
      <!-- 加载中 -->
      <template v-if="loading">
        <div style="padding: 60px 0; text-align: center; color: #B4BDCF;">加载协议中…</div>
      </template>

      <!-- 异常 -->
      <template v-else-if="errorMsg">
        <div class="cs-state">
          <div class="cs-state-ico err">!</div>
          <div class="cs-state-txt">{{ errorMsg }}</div>
        </div>
      </template>

      <!-- 协议内容 -->
      <template v-else>
        <div class="cs-head">
          <div class="cs-logo">协议签署</div>
          <div class="cs-title">{{ detail.title || '课程服务协议' }}</div>
          <div class="cs-sn">订单号：{{ detail.order_sn || '—' }}　{{ detail.product_name || '' }}</div>
        </div>

        <div class="cs-cust" v-if="detail.customer_name || detail.customer_phone">
          签署人：{{ detail.customer_name || '' }} <span v-if="detail.customer_phone" class="cs-phone">{{ detail.customer_phone }}</span>
        </div>

        <!-- 已签署提示 -->
        <div v-if="isSigned" class="cs-signed-bar">
          <svg viewBox="0 0 1024 1024" style="width: 18px; height: 18px;"><path fill="currentColor" d="M512 64a448 448 0 1 0 0 896 448 448 0 0 0 0-896zm-56 640L296 544l56-56 104 104 216-216 56 56-272 272z"/></svg>
          <span>本协议已于 {{ signTimeText }} 签署完成</span>
        </div>

        <!-- 协议正文 -->
        <div class="cs-content" v-html="renderContent(detail.content)"></div>

        <!-- 签字栏（正文末尾处）：甲方盖章(左) + 乙方签字(右) 并排，日期下一行 -->
        <div class="cs-footer-sign">
          <div class="cf-row">
            <div class="cf-cell">
              <div class="cf-label">甲方（盖章）</div>
              <div class="cf-box">
                <img v-if="detail.seal" :src="resolveAsset(detail.seal)" class="cf-seal-img" alt="甲方公章" />
                <span v-else class="cf-name">{{ companyName || '—' }}</span>
              </div>
            </div>
            <div class="cf-cell">
              <div class="cf-label">乙方（签字）</div>
              <div class="cf-box">
                <img v-if="signedSignature" :src="signedSignature" class="cf-sig-img" alt="乙方签名" />
                <span v-else class="cf-sig-empty">（未签字）</span>
              </div>
            </div>
          </div>
          <div class="cf-row date"><span class="cf-label">签署日期：</span>
            <span v-if="signTimeText" class="cf-date">{{ signTimeText }}</span>
            <span v-else-if="!isSigned" class="cf-date">{{ todayText }}</span>
          </div>
        </div>

        <!-- 签署操作条 -->
        <div v-if="!isSigned" class="cs-signbar">
          <div class="cs-signbar-party">
            <span class="cs-signbar-name">{{ detail.customer_name || '乙方' }}</span>
            <span class="cs-signbar-meta">签字：</span>
            <img v-if="signatureBase64" :src="signatureBase64" class="cs-sign-preview" alt="签名预览" />
            <span v-else class="cs-sign-empty">待签名</span>
          </div>
          <div class="cs-signbar-btns">
            <el-button size="small" class="cs-sign-btn warn" @click="openPad">签字</el-button>
            <el-button type="primary" size="small" class="cs-sign-btn" :disabled="!canSubmit" :loading="signing" @click="doSign">提交</el-button>
          </div>
        </div>
        <div v-else class="cs-signed-note">
          <div style="margin-bottom: 10px;">感谢您的信任，协议已完成签署</div>
          <a v-if="detail.pdf_url" class="cs-pdf-link" :href="resolveAsset(detail.pdf_url)" target="_blank">
            <svg viewBox="0 0 1024 1024" style="width: 16px; height: 16px; vertical-align: middle; margin-right: 5px;"><path fill="currentColor" d="M512 64L256 320h160v256h192V320h160L512 64zM192 640h640v128H192V640z"/></svg>
            查看已签署协议 PDF
          </a>
        </div>
      </template>
    </div>

    <!-- 手写签名弹层 -->
    <div v-if="padVisible" class="cs-mask" @click="closePad">
      <div class="cs-sheet" @click.stop>
        <div class="cs-sheet-title">手写签名</div>
        <div class="cs-sheet-warn">我确认以上操作为本人申请，并愿意承担相关法律责任。</div>
        <div class="cs-pad">
          <canvas ref="signCanvas" class="cs-canvas"></canvas>
        </div>
        <div class="cs-pad-hint">请使用正楷字体签上你的姓名「{{ detail.customer_name || '本人' }}」</div>
        <div class="cs-sheet-btns">
          <el-button size="small" @click="clearPad">清 空</el-button>
          <el-button type="primary" size="small" :disabled="!padHasStroke" @click="confirmPad">确 认</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { agreementQrView, agreementQrSign, agreementSavePdf } from '@/api/agreement'
import { API_BASE } from '@/config'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

const ASSET_BASE = API_BASE.replace(/\/api\/?$/, '')

export default {
  name: 'CustomerSign',
  data() {
    return {
      loading: true,
      errorMsg: '',
      detail: {},
      agree: false,
      signing: false,
      // 手写签名
      padVisible: false,
      signatureBase64: '',   // 新签名的 base64 原图
      submittedSignature: '', // 提交成功后的签名 base64（用于 PDF 生成，规避跨域）
      padHasStroke: false,
      // canvas 状态
      _ctx: null,
      _drawing: false,
      _lastX: 0,
      _lastY: 0
    }
  },
  computed: {
    isSigned() {
      return parseInt(this.detail.sign_status) === 2
    },
    // 已签署时展示的签名图（后端存的）
    signedSignature() {
      if (!this.detail.signature) return ''
      if (/^data:/.test(this.detail.signature)) return this.detail.signature
      if (/^https?:\/\//.test(this.detail.signature)) return this.detail.signature
      return ASSET_BASE + this.detail.signature
    },
    canSubmit() {
      return !!this.signatureBase64
    },
    signTimeText() {
      if (!this.detail.sign_time) return ''
      const d = new Date(parseInt(this.detail.sign_time) * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    todayText() {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    companyName() {
      return this.detail.company_name || ''
    }
  },
  created() {
    const { agreementId } = this.$route.params
    const token = this.$route.query.token || ''
    if (!agreementId || !token) {
      this.loading = false
      this.errorMsg = '签署链接无效，请重新获取'
      return
    }
    this.loadDetail(agreementId, token)
  },
  methods: {
    loadDetail(agreementId, token) {
      this.loading = true
      this.errorMsg = ''
      return agreementQrView(agreementId, token)
        .then(data => {
          this.detail = data || {}
          if (this.detail.sign_status === undefined) {
            this.errorMsg = '协议不存在或链接已失效'
          }
        })
        .catch(e => {
          this.errorMsg = (e && e.message) || '协议加载失败'
        })
        .finally(() => { this.loading = false })
    },
    /* ---- 手写签名 ---- */
    openPad() {
      this.padVisible = true
      this.signatureBase64 = ''
      this.padHasStroke = false
      this.$nextTick(() => this.initCanvas())
    },
    closePad() {
      if (this.padHasStroke) {
        this.$confirm('签名尚未确认，确定放弃本次签字？', '提示', {
          confirmButtonText: '放弃',
          cancelButtonText: '继续签名',
          type: 'warning'
        }).then(() => { this.padVisible = false }).catch(() => {})
      } else {
        this.padVisible = false
      }
    },
    initCanvas() {
      const canvas = this.$refs.signCanvas
      if (!canvas) return
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      const ctx = canvas.getContext('2d')
      this._ctx = ctx
      ctx.scale(dpr, dpr)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#18233C'
      ctx.lineWidth = 2.2
      // 白色底
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, rect.width, rect.height)
      // 轨迹事件
      canvas.addEventListener('pointerdown', this.onDown)
      canvas.addEventListener('pointermove', this.onMove)
      canvas.addEventListener('pointerup', this.onUp)
      canvas.style.touchAction = 'none'
    },
    onDown(e) {
      const rect = this.$refs.signCanvas.getBoundingClientRect()
      this._drawing = true
      this._lastX = e.clientX - rect.left
      this._lastY = e.clientY - rect.top
      this.padHasStroke = true
    },
    onMove(e) {
      if (!this._drawing || !this._ctx) return
      const rect = this.$refs.signCanvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      this._ctx.beginPath()
      this._ctx.moveTo(this._lastX, this._lastY)
      this._ctx.lineTo(x, y)
      this._ctx.stroke()
      this._lastX = x
      this._lastY = y
    },
    onUp() {
      this._drawing = false
    },
    clearPad() {
      const canvas = this.$refs.signCanvas
      if (!canvas) return
      const ctx = this._ctx
      const rect = canvas.getBoundingClientRect()
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, rect.width, rect.height)
      this.padHasStroke = false
    },
    confirmPad() {
      if (!this.padHasStroke) {
        return
      }
      const canvas = this.$refs.signCanvas
      if (!canvas) return
      // 导出白色背景的 PNG
      this.signatureBase64 = canvas.toDataURL('image/png')
      this.teardownCanvas()
      this.padVisible = false
    },
    teardownCanvas() {
      const canvas = this.$refs.signCanvas
      if (canvas) {
        canvas.removeEventListener('pointerdown', this.onDown)
        canvas.removeEventListener('pointermove', this.onMove)
        canvas.removeEventListener('pointerup', this.onUp)
      }
      this._ctx = null
    },
    /* ---- 提交签署 ---- */
    doSign() {
      if (!this.signatureBase64) {
        this.$message.warning('请先签名')
        return
      }
      const { agreementId } = this.$route.params
      const token = this.$route.query.token || ''
      this.signing = true
      agreementQrSign({ agreement_id: agreementId, token, signature: this.signatureBase64 })
        .then(() => {
          this.submittedSignature = this.signatureBase64
          this.$message.success('签署成功')
          this.signatureBase64 = ''
          this.loadDetail(agreementId, token)
            .then(() => {
              // 已签署态就绪后生成 PDF 存档（失败不阻塞）
              this.generateAndSavePdf(agreementId, token)
            })
            .catch(() => {})
        })
        .catch(e => {
          this.$message.error((e && e.message) || '签署失败，请重试')
        })
        .finally(() => { this.signing = false })
    },
    renderContent(html) {
      if (!html) return ''
      return String(html).replace(/\n/g, '<br/>')
    },
    // 后端相对路径资源 → 完整可访问地址
    resolveAsset(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url)) return url
      if (/^\//.test(url)) return ASSET_BASE + url
      return url
    },
    /* ---- 生成 PDF 存档并上传（样式与导出PDF一致） ---- */
    generateAndSavePdf(agreementId, token) {
      // 离屏容器，宽度对齐 A4@96dpi，供 html2canvas 捕获
      const holder = document.createElement('div')
      holder.style.cssText = 'position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-1;'
      holder.innerHTML = this.buildPdfHtml(this.detail || {})
      document.body.appendChild(holder)
      // 等图片（含印章、签名）加载完再截图
      const imgs = Array.from(holder.querySelectorAll('img'))
      return Promise.all(imgs.map(img => new Promise(res => {
        if (img.complete) return res()
        img.onload = res
        img.onerror = res
      })))
        .then(() => html2canvas(holder, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false }))
        .then(canvas => {
          const pdf = new jsPDF('p', 'mm', 'a4')
          const pw = pdf.internal.pageSize.getWidth()
          const ph = pdf.internal.pageSize.getHeight()
          const imgH = (canvas.height * pw) / canvas.width
          let left = imgH
          let y = 0
          const imgData = canvas.toDataURL('image/jpeg', 0.95)
          pdf.addImage(imgData, 'JPEG', 0, y, pw, imgH)
          left -= ph
          while (left > 0) {
            y -= ph
            pdf.addPage()
            pdf.addImage(imgData, 'JPEG', 0, y, pw, imgH)
            left -= ph
          }
          const pdfBase64 = pdf.output('datauristring').replace(/;filename=[^;]*/, '')
          return agreementSavePdf({ agreement_id: agreementId, token, pdf: pdfBase64 })
        })
        .then(res => {
          if (res && res.pdf_url) this.detail.pdf_url = res.pdf_url
          holder.remove()
        })
        .catch(e => {
          holder.remove()
          console.error('PDF 生成失败', e)
        })
    },
    /* 组装 PDF HTML（与协议列表导出PDF一致：标题居中、甲方盖章左+乙方签名右并排、签订日期） */
    buildPdfHtml(d) {
      const esc = v => this.escapeHtml(v)
      const title = d.title ? esc(d.title) : '协议'
      const sub = []
      if (d.order_sn) sub.push('订单号 ' + esc(d.order_sn))
      if (d.customer_name) sub.push(esc(d.customer_name))
      if (d.customer_phone) sub.push(esc(d.customer_phone))
      const sealBox = d && d.seal
        ? `<div style="display:inline-block;vertical-align:top;margin-right:40px;"><div style="font-size:15px;color:#18233C;margin-bottom:8px;">甲方盖章</div>` +
          `<img src="${this.resolveAsset(d.seal)}" style="max-width:200px;max-height:140px;border:1px dashed #E2E6EE;padding:8px;border-radius:8px;"/></div>` : ''
      const signSrc = this.submittedSignature || (d.signature ? this.resolveAsset(d.signature) : '')
      const signBox = signSrc
        ? `<div style="display:inline-block;vertical-align:top;"><div style="font-size:15px;color:#18233C;margin-bottom:8px;">乙方签名</div>` +
          `<img src="${signSrc}" style="max-width:280px;max-height:140px;border:1px dashed #E2E6EE;padding:8px;border-radius:8px;"/></div>` : ''
      const sealWrap = (signBox || sealBox) ? `<div style="margin-top:24px;">${sealBox}${signBox}</div>` : ''
      const signer = `<div style="margin-top:32px;border-top:1px solid #EEF1F6;padding-top:16px;line-height:2;font-size:13px;color:#2B3653;">
          <div>乙方：${esc(d.customer_name || '—')}</div>
          ${d && d.seal ? '<div>甲方盖章：见上方印章</div>' : ''}
          <div>签订日期：${this.pdfSignDate(d)}</div>
        </div>`
      return `
        <div style="font-family:'Microsoft YaHei','PingFang SC',sans-serif;color:#2B3653;padding:34px 40px;line-height:1.9;font-size:14px;background:#fff;">
          <div style="text-align:center;border-bottom:2px solid #3668FC;padding-bottom:14px;margin-bottom:22px;">
            <div style="font-size:20px;font-weight:700;color:#18233C;">${title}</div>
            <div style="font-size:12px;color:#8A94AD;margin-top:6px;">${sub.join(' · ')}</div>
          </div>
          ${this.renderContent(d.content || '')}
          ${sealWrap}
          ${signer}
        </div>`
    },
    escapeHtml(s) {
      if (s === undefined || s === null) return ''
      return String(s).replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ))
    },
    pdfSignDate(d) {
      if (d && d.sign_time) return this.signTimeText
      return this.todayText
    }
  }
}
</script>

<style scoped>
.customer-sign {
  min-height: 100vh;
  background: linear-gradient(160deg, #F0F3FA 0%, #E8EDF7 100%);
  padding: 40px 16px;
  display: flex;
  justify-content: center;
}
.cs-card {
  width: 100%;
  max-width: 760px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(54, 104, 252, 0.08);
  padding: 28px 32px 40px;
  position: relative;
  min-height: 300px;
}
.cs-head { text-align: center; padding-bottom: 18px; border-bottom: 1px dashed #E5E9F2; }
.cs-logo { display: inline-block; font-size: 12px; color: #fff; background: #3668FC; padding: 3px 12px; border-radius: 20px; letter-spacing: 2px; }
.cs-title { font-size: 20px; font-weight: 700; color: #18233C; margin-top: 12px; }
.cs-sn { font-size: 12px; color: #8A94AD; margin-top: 6px; font-family: monospace; }
.cs-cust { display: flex; align-items: center; gap: 6px; font-size: 14px; color: #2B3653; padding: 14px 0 4px; font-weight: 600; }
.cs-phone { font-weight: 400; color: #8A94AD; font-size: 12px; }
.cs-signed-bar {
  display: flex; align-items: center; gap: 6px;
  margin: 12px 0; padding: 10px 14px;
  background: #EAF7F0; color: #2BB673;
  border-radius: 8px; font-size: 13px; font-weight: 600;
}
.cs-content {
  padding: 20px 24px; margin-top: 12px;
  line-height: 1.9; font-size: 14px; color: #2B3653;
  white-space: normal; word-break: break-word;
}
.cs-content :deep(p) { margin: 0 0 12px; }
.cs-content :deep(img) { max-width: 100%; height: auto; display: block; margin: 0 auto; }
.cs-content :deep(table) { max-width: 100%; }

/* 正文末尾签字栏：甲方盖章(左) + 乙方签字(右) 并排，无边框 */
.cs-footer-sign {
  margin-top: 18px;
  padding: 0 4px;
  font-size: 14px;
  color: #2B3653;
}
.cf-row { display: flex; gap: 16px; flex-wrap: nowrap; }
.cf-cell { flex: 1 1 0; min-width: 0; }
.cf-label { color: #5A6580; font-size: 13px; }
.cf-box { margin-top: 10px; display: flex; align-items: flex-start; min-height: 60px; }
.cf-name { font-weight: 600; }
.cf-sig-img { display: block; max-width: 100%; max-height: 90px; border: 1px solid #E5E9F2; border-radius: 4px; }
.cf-sig-empty { color: #B4BDCF; }
.cf-seal-img { display: block; max-width: 100%; max-height: 90px; border-radius: 4px; }
.cf-row.date { margin-top: 14px; display: flex; align-items: center; }
.cf-row.date .cf-label { margin-right: 8px; }
.cf-date { color: #2B3653; }

/* 底部签署操作条 */
.cs-signbar {
  margin-top: 16px;
  background: #F7F9FC;
  border: 1px solid #E5E9F2;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.cs-signbar-party { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #18233C; flex: 1; min-width: 160px; }
.cs-signbar-name { font-weight: 600; }
.cs-signbar-meta { color: #5A6580; }
.cs-sign-preview { height: 36px; border: 1px solid #E5E9F2; border-radius: 4px; }
.cs-sign-empty { color: #B4BDCF; font-size: 13px; }
.cs-signbar-btns { display: flex; gap: 8px; }
.cs-sign-btn.warn { background: #FFF4E6; border-color: #FFC069; color: #D98A1F; }

/* 手写签名弹层 */
.cs-mask {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: flex-end; justify-content: center;
}
.cs-sheet {
  width: 100%; max-width: 760px;
  background: #fff;
  border-radius: 14px 14px 0 0;
  padding: 18px 16px calc(24px + env(safe-area-inset-bottom, 0));
}
.cs-sheet-title { font-size: 16px; font-weight: 700; color: #18233C; text-align: center; }
.cs-sheet-warn {
  margin-top: 12px; padding: 9px 12px;
  background: #FFF7E6; color: #B26A00;
  border-radius: 6px; font-size: 12px; line-height: 1.5;
}
.cs-pad {
  margin-top: 12px;
  border: 1px solid #E5E9F2;
  border-radius: 8px;
  overflow: hidden;
}
.cs-canvas { width: 100%; height: 200px; display: block; cursor: crosshair; background: #fff; }
.cs-pad-hint { margin-top: 8px; font-size: 12px; color: #8A94AD; text-align: center; }
.cs-sheet-btns { margin-top: 14px; display: flex; gap: 10px; justify-content: center; }
.cs-sheet-btns .el-button { width: 110px; }

.cs-signed-note { text-align: center; color: #2BB673; font-size: 13px; margin-top: 18px; }
.cs-pdf-link { display: inline-block; color: #3668FC; font-weight: 600; text-decoration: none; }
.cs-pdf-link:hover { text-decoration: underline; }
.cs-state { padding: 80px 0; text-align: center; }
.cs-state-ico {
  width: 56px; height: 56px; margin: 0 auto 16px;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 28px; font-weight: 700;
}
.cs-state-ico.err { background: #FDECEC; color: #E5484D; }
.cs-state-txt { font-size: 14px; color: #5A6580; }

::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
@media (max-width: 560px) {
  .cs-card { padding: 20px 18px 28px; }
}
</style>